/**
 * ConsentManagementEditor Component
 *
 * Editor for funnel-level GDPR consent configuration.
 *
 * Behavior:
 * - Shows "not configured" state when value is undefined (no GDPR for this funnel)
 * - "Enable GDPR Consent" button activates config with defaults and shows form immediately
 * - Consent form fields are shown directly without additional toggle (UX improvement)
 * - "Disable GDPR for this funnel" button clears config (sets to undefined)
 *
 * Pattern: Follows SEOEditor (synchronous onChange, no async save)
 *
 * @module webplacements/components/editors
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ConsentManagementConfig } from '@modules/analytics-settings/types/analytics.types';
import { DEFAULT_CONSENT_CONFIG } from '@modules/analytics-settings/types/analytics.types';

export interface ConsentManagementEditorProps {
  /** Current funnel-level consent config (undefined = GDPR not needed) */
  value: ConsentManagementConfig | undefined;
  /** Called when config changes */
  onChange: (config: ConsentManagementConfig | undefined) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

/**
 * ConsentManagementEditor Component
 *
 * Funnel-level GDPR consent configuration.
 */
export const ConsentManagementEditor: React.FC<ConsentManagementEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();

  // Handler: Enable GDPR (create default config)
  const handleEnable = () => {
    onChange(DEFAULT_CONSENT_CONFIG);
  };

  // Handler: Disable GDPR (clear config)
  const handleDisable = () => {
    onChange(undefined);
  };

  // Handler: Update field
  const handleFieldChange = (field: keyof ConsentManagementConfig) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    if (!value) return;
    onChange({
      ...value,
      [field]: event.target.value,
    });
  };

  // === NOT CONFIGURED STATE ===
  if (!value) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>GDPR consent is not configured for this funnel</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analytics will load immediately without a consent banner. This is suitable for
            audiences that don't require GDPR compliance (e.g., US-only funnels).
          </Typography>
        </Alert>

        <Button
          variant="contained"
          onClick={handleEnable}
          disabled={disabled}
          fullWidth
        >
          Enable GDPR Consent
        </Button>

        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: theme.palette.grey[100],
            borderRadius: 1,
            border: `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>Use Case:</strong> Enable GDPR consent for EU-targeted funnels or when you need
            user permission before loading analytics. Each funnel can have independent consent settings.
          </Typography>
        </Box>
      </Box>
    );
  }

  // === CONFIGURED STATE ===
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          GDPR Consent Settings
        </Typography>
        <Tooltip title="Consent banner will be shown to users before analytics are loaded">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>

      {/* Consent Form Fields - shown directly without toggle */}
      <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Users must accept the consent banner before analytics are loaded.
          </Alert>

          {/* Banner Text */}
          <TextField
            label="Banner Text"
            value={value.bannerText ?? ''}
            onChange={handleFieldChange('bannerText')}
            disabled={disabled}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 3 }}
            placeholder="We use cookies to improve your experience and analyze site usage."
            size="small"
          />

          {/* Privacy Policy URL */}
          <TextField
            label="Privacy Policy URL"
            value={value.privacyPolicyUrl ?? ''}
            onChange={handleFieldChange('privacyPolicyUrl')}
            disabled={disabled}
            fullWidth
            sx={{ mb: 3 }}
            placeholder="https://example.com/privacy"
            type="url"
            size="small"
          />

          {/* Default Consent */}
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Default Consent (before user interaction)</InputLabel>
            <Select
              value={value.defaultConsent}
              onChange={(e) => {
                if (!value) return;
                onChange({
                  ...value,
                  defaultConsent: e.target.value as 'granted' | 'denied',
                });
              }}
              disabled={disabled}
              label="Default Consent (before user interaction)"
            >
              <MenuItem value="denied">
                <Box>
                  <Typography variant="body2">Denied (Recommended)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Analytics blocked until user accepts
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="granted">
                <Box>
                  <Typography variant="body2">Granted</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Analytics run immediately, user can opt-out
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
      </Box>

      {/* Disable Button */}
      <Button
        variant="outlined"
        color="error"
        onClick={handleDisable}
        disabled={disabled}
        fullWidth
        sx={{ mt: 2 }}
      >
        Disable GDPR for this funnel
      </Button>

      {/* Info Box */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: theme.palette.info.main + '10',
          borderRadius: 1,
          border: `1px solid ${theme.palette.info.main}30`,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          GDPR settings are baked into the funnel when published. Changes require republishing to take effect.
        </Typography>
      </Box>
    </Box>
  );
};

export default ConsentManagementEditor;
